import type { SolarProposalEquipmentDto } from "~/api/dto/solar-proposal-equipment.dto"
import componentBattery from "~/assets/photo/solar-proposal/component-battery.png"
import componentInverter from "~/assets/photo/solar-proposal/component-inverter.png"
import componentPanels from "~/assets/photo/solar-proposal/component-panels.jpg"
import vendorNode from "~/assets/photo/solar-proposal/vendor-node.jpg"

import type { SolarComponent } from "./solar-presentation"
import type { SolarPresentationAiCompute } from "./solar-presentation-data"
import { equipmentLabel, formatNumber } from "./solar-proposal-design"
import { batteryProductShot } from "../ProposalPresentation/battery-product-shot"

type Stat = SolarComponent[`stats`][number]

/** A stat whose value was never stored is left out; the row shrinks. */
const stat = (
  value: number | null,
  format: (value: string) => string,
  label: string,
): Stat | null =>
  value === null ? null : { value: format(formatNumber(value, 2)), label }

const present = <T>(items: (T | null)[]): T[] =>
  items.filter((item): item is T => item !== null)

/** "{manufacturer} {name}", with the product name in the gradient. */
const title = (
  manufacturer: string | null,
  name: string,
): Pick<SolarComponent, `highlight` | `name`> => {
  const label = equipmentLabel(manufacturer, name)
  const prefix = manufacturer ? `${manufacturer} ` : ``

  const product =
    prefix && label.toLowerCase().startsWith(prefix.toLowerCase())
      ? label.slice(prefix.length)
      : label

  return { name: label, highlight: product || label }
}

/**
 * One slide per kind the design has, specs only: real proposals carry no
 * product copy, so summary and features stay empty. Formatting only; every
 * figure arrives from the equipment read.
 */
export const toSolarComponents = ({
  panels,
  inverter,
  storage,
}: SolarProposalEquipmentDto): SolarComponent[] => {
  const components: SolarComponent[] = []

  if (panels) {
    const heading = title(panels.manufacturer, panels.name)
    components.push({
      id: `panels`,
      kind: `Panels`,
      ...heading,
      tag: present([
        panels.quantity === null
          ? null
          : `${formatNumber(panels.quantity, 0)}-panel array`,
        panels.arraySizeKw === null
          ? null
          : `${formatNumber(panels.arraySizeKw, 2)} kW`,
      ]).join(` · `),
      image: componentPanels,
      imageAlt: heading.name,
      imageScale: 0.72,
      summary: null,
      stats: present([
        stat(panels.wattage, (value) => `${value}W`, `Per panel`),
        stat(panels.quantity, (value) => value, `Panels`),
        stat(panels.arraySizeKw, (value) => `${value} kW`, `Array size`),
      ]),
      features: [],
    })
  }

  if (inverter) {
    const { secondary } = inverter
    const heading = title(inverter.manufacturer, inverter.name)
    components.push({
      id: `inverter`,
      kind: `Inverter`,
      ...heading,
      tag: [
        `×${inverter.quantity}`,
        secondary
          ? `with ${equipmentLabel(secondary.manufacturer, secondary.name)} ×${secondary.quantity}`
          : null,
      ]
        .filter(Boolean)
        .join(` · `),
      image: componentInverter,
      imageAlt: heading.name,
      imageScale: 1,
      summary: null,
      stats: present([
        stat(
          inverter.cecEfficiencyPct,
          (value) => `${value}%`,
          `CEC efficiency`,
        ),
        stat(inverter.acRatingKw, (value) => `${value} kW`, `AC rating`),
        stat(inverter.quantity, (value) => value, `Units`),
      ]),
      features: [],
    })
  }

  if (storage) {
    // One package that is both battery and inverter (Powerwall 3) shows once.
    const combined = storage.inverter
    const heading = title(storage.manufacturer, storage.name)
    const shot = batteryProductShot(storage.manufacturer, storage.name)
    components.push({
      id: `storage`,
      kind: combined ? `Storage + inverter` : `Storage`,
      ...heading,
      tag: [
        `×${storage.quantity}`,
        storage.backupCapable ? `backup-ready` : null,
      ]
        .filter(Boolean)
        .join(` · `),
      image: shot?.src ?? componentBattery,
      imageAlt: shot?.alt ?? heading.name,
      imageScale: 1,
      summary: null,
      stats: present([
        stat(storage.usableKwh, (value) => `${value} kWh`, `Usable capacity`),
        // A package's AC rating is its continuous output, so it takes that stat.
        combined && combined.acRatingKw !== null
          ? stat(combined.acRatingKw, (value) => `${value} kW`, `AC rating`)
          : stat(storage.continuousKw, (value) => `${value} kW`, `Continuous`),
        combined
          ? stat(
              combined.cecEfficiencyPct,
              (value) => `${value}%`,
              `CEC efficiency`,
            )
          : null,
        storage.warrantyYears === null
          ? stat(
              storage.roundTripEfficiencyPct,
              (value) => `${value}%`,
              `Round-trip`,
            )
          : stat(storage.warrantyYears, (value) => `${value} yr`, `Warranty`),
      ]),
      features: [],
    })
  }

  return components
}

/** The compute node's slide, from the Compute read's package details. */
export const toAiComputeComponent = ({
  name,
  computerData,
}: SolarPresentationAiCompute): SolarComponent => ({
  id: `ai-compute`,
  kind: `Compute node`,
  name: `Vendor ${name}`,
  highlight: name,
  tag: `In partnership with Vendor®`,
  image: vendorNode,
  imageAlt: `Vendor compute node, wall-mounted`,
  imageScale: 1,
  summary: null,
  stats: computerData
    ? present([
        computerData.gpuArchitecture
          ? { value: computerData.gpuArchitecture, label: `GPU` }
          : null,
        {
          value: `${formatNumber(computerData.gpuMemoryGb, 0)} GB`,
          label: `GPU memory`,
        },
        { value: formatNumber(computerData.gpusPerNode, 0), label: `GPUs` },
      ])
    : [],
  features: [],
})
